import Vue from 'vue'
import router from '@/router'
import {http} from "@/axios"
import {setMetaRobots} from "@/helpers/metaRobots"
import {removeCanonicalTag} from "@/router/common/setCanonical"

import responses from './responses';

function getInitState() {
  return {
    loaded: false,
    pending: false,
    error: null,

    popularFilters: null,
    dynamicFilters: null,
    vacancies: null,
    indexing: null,
    total: 0,
    perPage: 18,
    subscribed: null,
    title: null,

    profession: null,
    directions: null,
    country: null,
    city: null,

    metaTitle: null,
    metaDescription: null,

    staticRouteFilters: {},
    dynamicRouteFilters: [],

    staticFiltersKeys: {
      COUNTRY: 'country',
      CITY: 'city',
    },

    otherKeys: {
      SORT: 'order_by',
      SEARCH: 'q',
      CATEGORIES: 'categories',
      PAGE: 'page',
    },

    noIndexKeys: [
      'directions',
      'customer_id',
      'direction',
    ]
  }
}

export default {
  namespaced: true,
  modules: {
    responses,
  },
  state: getInitState(),
  getters: {
    params(state) {
      const $route = router.currentRoute
      const {
        staticRouteFilters,
        otherKeys,
        perPage
      } = state
      const queriesList = [otherKeys.SORT, otherKeys.SEARCH, otherKeys.PAGE]
      const directions = $route.query.categories
      const staticRouteFiltersFormat = {}

      for (const key in staticRouteFilters) {
        staticRouteFiltersFormat[key] = staticRouteFilters[key] && staticRouteFilters[key][0]
      }

      const params = {
        customer_id: $route.query.customer_id,
        ...staticRouteFiltersFormat,

        params: state.dynamicRouteFilters,

        profession: getProfessionSlugFromRoute($route.params.professionsIds),
        directions: directions ? (Array.isArray(directions) ? directions : directions.split('|')) : null,

        limit: perPage
      }

      queriesList.forEach(query => {
        params[query] = $route.query[query]
      })

      params.url = $route.fullPath

      return params
    },
    dynamicFiltersItems(state) {
      const allFilters = state.dynamicFilters
      const routeFilters = [...state.dynamicRouteFilters]

      if (!allFilters || !allFilters.length) return []

      const result = []

      groupLabel: for (let group of allFilters) {
        for (const item of group.data) {
          const index = routeFilters.indexOf(item.slug)
          if (index > -1) {
            group = JSON.parse(JSON.stringify(group))
            delete group.data

            result.push({
              ...item,
              group
            })

            routeFilters.splice(index, 1)

            if (!routeFilters.length) {
              break groupLabel
            }
          }
        }
      }

      return result
    },
    hasStaticRouteFilters(state) {
      return !!(
        state.staticRouteFilters
        && Object.keys(state.staticRouteFilters).length
      )
    },
    hasDynamicRouteFilters(state) {
      return state.dynamicRouteFilters && state.dynamicRouteFilters.length
    },
  },
  mutations: {
    setProperty(state, [key, value]) {
      if (!state.hasOwnProperty(key)) return
      state[key] = value
    },
    setVacancyProperty(state, [index, key, value]) {
      if (!state.vacancies[index] || !state.vacancies[index].hasOwnProperty(key)) return
      Vue.set(state.vacancies[index], key, value)
    },
    setError(state, errorObj) {
      if (!errorObj) {
        return state.error = null
      }

      state.error = {
        code: errorObj.code,
        message: errorObj.message
      }
    },
    updateRouteFilters(state, $route) {
      state.staticRouteFilters = getFiltersStatic($route.params.staticFilters || null)
      state.dynamicRouteFilters = getFilters($route.params.dynamicFilters || null)
    },
    // setSelectedFilters(state, filtersSlugs) {
    //
    // }
  },
  actions: {
    load({state, getters, commit}) {
      const params = getters['params']

      commit('setProperty', ['pending', true])

      http.get('/api/vacancies-list-v2/get', {
        params
      }).then(({data}) => {
        commit('setProperty', ['popularFilters', data.filters.popular_filters])
        commit('setProperty', ['dynamicFilters', data.filters.dynamic_filters])
        commit('setProperty', ['profession', data.filters.profession])
        commit('setProperty', ['directions', data.filters.directions])
        commit('setProperty', ['country', data.filters.country])
        commit('setProperty', ['city', data.filters.city])
        commit('setProperty', ['vacancies', data.data.data])
        commit('setProperty', ['indexing', data.index])
        commit('setProperty', ['total', data.data.total])
        commit('setProperty', ['subscribed', data.subscribed])
        commit('setProperty', ['title', data.title || null])
        commit('setProperty', ['metaTitle', data.meta_title || null])
        commit('setProperty', ['metaDescription', data.meta_description || null])
        commit('setProperty', ['loaded', true])

        const hasSearch = !!params[state.otherKeys.SEARCH]

        if (!data.data.total) {
          setMetaRobots('noindex, follow', 404, hasSearch)
        }

        if (data.index === false) {
          setMetaRobots('noindex, follow', null, hasSearch)
        }

        const noIndexKeys = state.noIndexKeys
        const hasNoIndexKey = noIndexKeys.some(key => {
          const value = params[key]
          if (!value) return false

          if (Array.isArray(value)) {
            return value.length
          }

          return true
        })

        if (hasNoIndexKey) {
          setMetaRobots('noindex, follow', hasSearch)
          removeCanonicalTag()
        }

        // this.loadedTitle =  data.h1_title || null
      }).catch(error => {
        commit('setError', {
          code: error.response && error.response.status,
          message: error.response && error.response.data && error.response.data.message || error.message
        })
      }).finally(() => {
        commit('setProperty', ['pending', false])
      })
    },
    async getAvailableForPublication() {
      const { data } = await http.get('/api/cabinet/employer/subscribe/publication-stats');
      return data?.data?.publication_count;
    },
    async getVacancyTags(_, { vacancy_id }) {
      const { data } = await http.get('/api/cabinet/employer/my-responses/get-vacancy-tags', { params: { vacancy_id } });
      return data?.data;
    },
    async getVacancyPosition(_, { vacancyId }) {
      const { data } = await http.get(`/api/cabinet/vacancy-position/${vacancyId}`);
      return data?.data;
    },
  }
}

function getFilters(filters) {
  if (!filters) return []
  return filters.split('-')
}

function getFiltersStatic(filters) {
  if (!filters) return {}

  if (typeof filters === 'string') {
    filters = filters.split('/')
  }

  return filters.reduce((result, item) => {
    let [key, value] = item.split('-')
    value = value.split('|')

    result[key] = value

    return result
  }, {})
}

function getProfessionSlugFromRoute(arr) {
  if (Array.isArray(arr)) {
    return arr[0] || null
  }

  return arr || null
}
